/**
 * Search Workspace Products Server Function
 *
 * Performs server-side search with pagination.
 */

import { createServerFn } from "@tanstack/react-start";
import { searchProductsApiWorkspacesWorkspaceIdProductsGet } from "@/lib/api/generated/products/products";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError, logInfo } from "@/lib/structured-log";
import {
  normalizeProductListResponse,
  type SerializableProductListResponse,
} from "@/server/products/serializable-product";

interface SearchWorkspaceProductsServerRequest {
  workspaceId: string;
  request: {
    name?: string;
    product_status?: string;
    source?: string;
    page?: number;
    page_size?: number;
  };
}

export const searchWorkspaceProductsServer = createServerFn({ method: "GET" })
  .validator((data: SearchWorkspaceProductsServerRequest) => data)
  .handler(async ({ data }): Promise<SerializableProductListResponse> => {
    const { workspaceId, request } = data;

    try {
      // Log the incoming request for debugging
      logInfo("products.search.request", {
        workspaceId,
        request: {
          name: request.name,
          product_status: request.product_status,
          source: request.source,
          page: request.page,
          page_size: request.page_size,
        },
      });

      const response = await searchProductsApiWorkspacesWorkspaceIdProductsGet(workspaceId, {
        name: request.name,
        product_status: request.product_status,
        source: request.source,
        page: request.page ?? 1,
        page_size: request.page_size ?? 100,
      });

      if (response.status !== 200) {
        const errorMessage = extractApiErrorMessage(response, "Failed to search products");
        logError("products.search.failed", { error: errorMessage });
        throw new Error(errorMessage);
      }

      logInfo("products.search.completed", {
        returned: response.data.products.length,
        total: response.data.total,
      });

      return normalizeProductListResponse(response.data);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        "An error occurred while searching products",
      );
      logError("products.search.error", { error: errorMessage });
      throw new Error(errorMessage);
    }
  });
